import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useAuth } from "@/hooks/useAuth";
import { startExperienceAttempt, type ExperienceOrigin } from './experienceTelemetry';
import { automaticRetryDelay, classifyRequestError, GemaRequestError } from './requestError';
import { request } from "./progressApi";
import { warmGemaProgress, clearGemaProgressWarmup } from "./progressWarmup";
import { trackApp } from "@/lib/appAnalytics";

import { Context, type GemaExperienceStatus, type GemaExperienceState } from "./GemaExperienceContext";
export { useGemaExperience } from "./GemaExperienceContext";
export type { GemaExperienceState, GemaAudience } from "./GemaExperienceContext";
const pendingKey = (userId: string) => `ondiet:gema-launch-v1:pending:${userId}`;
const hasPending = (userId: string) => { try { return localStorage.getItem(pendingKey(userId)) === "1"; } catch { return false; } };
const savePending = (userId: string, pending: boolean) => { try { if (pending) localStorage.setItem(pendingKey(userId), "1"); else localStorage.removeItem(pendingKey(userId)); } catch { /* best effort */ } };
function valid(value: GemaExperienceState): boolean {
  return !!value && typeof value.enabled === 'boolean' && value.version === 'gema-launch-v1' &&
    ['existing','new','pending'].includes(value.audience) && typeof value.introductionCompleted === 'boolean';
}

export function GemaExperienceProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  return <AccountExperience key={user?.id ?? 'signed-out'} userId={user?.id}>{children}</AccountExperience>;
}
function AccountExperience({userId, children}: {userId?: string; children: ReactNode}) {
  const [experience, setExperience] = useState<GemaExperienceState | null>(null);
  const [status, setStatus] = useState<GemaExperienceStatus>("loading");
  const availability = useRef<GemaExperienceStatus>("loading");
  const setAvailability = useCallback((next: GemaExperienceStatus) => { availability.current = next; setStatus(next); }, []);
  const current = useRef<GemaExperienceState | null>(null);
  const active = useRef(true);
  const reading = useRef(false);
  const requestController = useRef<AbortController | null>(null);
  const retryNotBefore = useRef(0);
  const reportedFailure = useRef(false);
  const writing = useRef<Promise<boolean> | null>(null);
  const completedLocally = useRef(false);
  const exposed = useRef(false);
  const sync = useCallback((): Promise<boolean> => {
    if (!userId || !current.current?.enabled || availability.current !== "ready" || !active.current) return Promise.resolve(false);
    if (writing.current) return writing.current;
    writing.current = (async () => {
      try {
        const result = await request('/experience', 'PUT', {version:'gema-launch-v1'});
        if (!active.current || availability.current !== 'ready' || !current.current?.enabled || result?.version !== 'gema-launch-v1' || result.introductionCompleted !== true) return false;
        savePending(userId, false);
        return true;
      } catch { return false; }
      finally { writing.current = null; }
    })();
    return writing.current;
  }, [userId]);
  const refreshFrom = useCallback(async (origin: ExperienceOrigin) => {
    if (!userId || import.meta.env.VITE_FEATURE_RAMON_ONBOARDING !== 'true') { setAvailability("ready"); return; }
    if (reading.current || !active.current || Date.now() < retryNotBefore.current) return;
    reading.current = true;
    const controller = new AbortController();
    requestController.current = controller;
    try {
      for (let attempt = 0; attempt < 2; attempt++) {
        const telemetry = startExperienceAttempt(attempt === 0 ? origin : 'automatic', attempt + 1);
        let failure: GemaRequestError | undefined;
        try {
          const result: GemaExperienceState = await request('/experience', 'GET', undefined, 2500, { signal: controller.signal, onPhase: telemetry.phase });
          if (!active.current || controller.signal.aborted) throw new GemaRequestError('cancelled', 'fetch');
          telemetry.phase('validation');
          if (!valid(result)) throw new GemaRequestError('invalid', 'validation');
          if (!current.current && result.enabled) warmGemaProgress(userId);
          current.current = {...result, introductionCompleted: result.introductionCompleted || completedLocally.current || hasPending(userId)};
          setExperience(current.current);
          setAvailability("ready");
          if (result.enabled && !exposed.current) { exposed.current = true; trackApp('gema_exposed',{version:result.version,source:result.audience}); }
          if (result.enabled && hasPending(userId)) void sync();
        } catch (error) {
          failure = classifyRequestError(error, 'fetch');
          if (active.current && !controller.signal.aborted) setAvailability("unavailable");
        }
        const cancelled = controller.signal.aborted || !active.current;
        const report = !!failure && !cancelled && !reportedFailure.current;
        if (report) reportedFailure.current = true;
        telemetry.end(failure, report);
        if (!failure || cancelled) break;
        if (failure.retryAfterMs !== undefined) retryNotBefore.current = Date.now() + failure.retryAfterMs;
        const delay = automaticRetryDelay(failure);
        if (attempt > 0 || delay === null) break;
        // Keep reading=true during backoff: manual/focus/interval events coalesce.
        await new Promise<void>(resolve => {
          const finish = () => { window.clearTimeout(timer); controller.signal.removeEventListener('abort', finish); resolve(); };
          const timer = window.setTimeout(finish, delay);
          controller.signal.addEventListener('abort', finish, { once: true });
        });
        if (controller.signal.aborted || !active.current) break;
      }
    } finally {
      if (requestController.current === controller) { requestController.current = null; reading.current = false; }
    }
  }, [userId, sync, setAvailability]);
  const refresh = useCallback(() => refreshFrom('manual'), [refreshFrom]);
  const complete = useCallback(async () => {
    if (!userId || !current.current?.enabled || availability.current !== "ready" || !active.current) return false;
    if (!current.current.introductionCompleted) {
      completedLocally.current = true;
      current.current = {...current.current, introductionCompleted:true};
      setExperience(current.current);
      savePending(userId,true);
      trackApp('gema_intro_completed',{version:'gema-launch-v1',source:current.current.audience});
    }
    return sync();
  }, [userId, sync, setAvailability]);
  useEffect(() => {
    active.current = true;
    void refreshFrom('initial');
    const focus = () => { if (!document.hidden) void refreshFrom('focus'); };
    const online = () => { if (!document.hidden) void refreshFrom('online'); };
    const visible = () => { if (!document.hidden) void refreshFrom('visibility'); };
    window.addEventListener('focus', focus);
    window.addEventListener('online', online);
    document.addEventListener('visibilitychange', visible);
    const timer = window.setInterval(() => { if (!document.hidden) void refreshFrom('interval'); },60_000);
    return () => {
      active.current=false; requestController.current?.abort(); requestController.current=null; reading.current=false;
      clearGemaProgressWarmup(); window.clearInterval(timer);
      window.removeEventListener('focus',focus); window.removeEventListener('online',online); document.removeEventListener('visibilitychange',visible);
    };
  }, [refreshFrom]);
  const loading = status === "loading";
  const value = useMemo(() => ({experience,status,loading,complete,refresh}),[experience,status,loading,complete,refresh]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
}
